import { Navigate, useParams } from 'react-router-dom';
import { Col, Result, Row, Skeleton, Space } from 'antd';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useGetToolOverviewByBarcodeQuery } from 'app/services/tools';
import { Paths } from 'constants/index';
import Details from './components/Details';
import Tabs from './components/Tabs';

// Readonly, unauthenticated-safe overview shown when a tool QR is scanned by a phone
// camera or the in-app scanner. Reuses the exact same Details/Tabs components as the
// private Tools/:id view (via their `readonly` prop) - only the audit Logs section and
// edit/delete Actions are excluded. Everything else (info card, maintenance history,
// test history, project/site history, row-click view drawers) is identical.
// When the caller is authenticated AND authorized for the tool's unit, the backend
// includes ToolId and we redirect straight to the private tool detail page.
const ToolPublicOverview = () => {
  const { t } = useTranslation();
  const { barcode } = useParams<{ barcode: string }>();
  const { data, isLoading, isFetching, isError } = useGetToolOverviewByBarcodeQuery(barcode ?? '', { skip: !barcode });

  if (isLoading || isFetching) return <Skeleton active />;

  if (isError || !data || !data.Found) {
    return <Result status="404" title={t('pages.tools.overviewnotfound')} />;
  }

  if (data.ToolId) {
    return <Navigate to={Paths.TOOLS_VIEW.replace(':id', data.ToolId.toString())} replace />;
  }

  return (
    <>
      <Helmet title={`Kvalilog > ${t('pages.tools.overview')}`} />
      <Space direction="vertical" size="middle" className="antd-space" style={{ display: 'flex' }}>
        <Row>
          <Col span={24}>
            <Details tool={data} readonly />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Tabs readonly maintenances={data.Maintenances} tests={data.Tests} projects={data.Projects} />
          </Col>
        </Row>
      </Space>
    </>
  );
};

export default ToolPublicOverview;
